import firebase from "firebase/app";

import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import updateArticle from "astrid-firestore/src/api/articles/updateArticle";
import artifactProductionStatuses from "astrid-firestore/src/api/artifacts/constants/artifactProductionStatuses";
import artifactValidationStatuses from "astrid-firestore/src/api/artifacts/constants/artifactValidationStatuses";
import recallArtifact from "astrid-firestore/src/api/artifacts/recallArtifact";
import softDeleteDocument from "astrid-firestore/src/api/documents/softDeleteDocument";
import { runBatch } from "astrid-firestore/src/helpers/index";
import getCollectionData from "astrid-firestore/src/utils/getCollectionData";

import { useProfile } from "../../authentication/state/profile";
import useHasRight from "../../authorization/hooks/useHasRight";

import getArtifactProductionStatus from "../utils/getArtifactProductionStatus";

export default function useArtifactDropdownMenuOptions({ article, disabled, production }) {
	const { t } = useTranslation();

	const user = useProfile();
	const hasRight = useHasRight();

	return (artifact) => [
		{
			text:
				artifact.id !== article?.artifact?.id
					? t("selectAsDefault", "Select as default")
					: t("unselect", "Unselect"),
			hidden: !article,
			action: () => {
				return updateArticle(firebase, {
					ref: article.ref,
					artifact: artifact.id !== article.artifact?.id ? artifact : null,
				});
			},
		},
		{
			text: t("recall", "Recall"),
			disabled: getArtifactProductionStatus({ artifact }) !== artifactProductionStatuses.DELIVERED,
			confirmText: t("recallArtifactConfirmation", "Are you sure you want to recall delivery of this artifact?"),
			hidden: !production,
			action: () => {
				return recallArtifact(firebase, { artifact });
			},
		},
		{
			text: t("validate", "validate"),
			disabled: disabled || artifact.status === artifactValidationStatuses.PENDING,
			action: () => {
				return artifact.ref.update({
					status: artifactValidationStatuses.PENDING,
				});
			},
		},
		{
			text: t("triggerSilenceAnalysis", "Silence analysis"),
			hidden: !hasRight("admin"),
			action: async () => {
				const files = await getCollectionData(artifact.ref.collection("files"));
				const filtered = files.filter((file) => !file.silences?.length);

				return runBatch(db, (batch) => {
					for (const file of filtered) {
						batch.update(file.ref, { silences: null });
					}
				});
			},
		},
		{
			text: t("delete", "Delete"),
			hidden: !hasRight("artifacts.delete"),
			disabled: disabled || artifact.id === article?.artifact?.id || (production && artifact.work),
			confirmText: t("deleteArtifactConfirmation", "Are you sure you want to delete this artifact?"),
			action: () => {
				return softDeleteDocument(firebase, artifact, user);
			},
		},
	];
}
